import React, { useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, CheckIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import MetricsChart from '../shared/MetricsChart';

// Add API base URL constant
// const API_BASE_URL = 'http://localhost:5669/streaming';
const API_BASE_URL = process.env.REACT_APP_API_URL + '/streaming';

function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}

function formatPercent(num, showPlus = false) {
  if (num === null || num === undefined) return '';
  return `${showPlus && num > 0 ? '+' : ''}${num.toFixed(1)}%`;
}

function getPercentChangeColor(percent) {
  if (percent === null || percent === undefined) return 'bg-gray-50';
  
  // Get the magnitude and determine color intensity
  const magnitude = Math.abs(percent);
  
  // More gradual scale with logarithmic-like progression
  if (percent > 0) {
    if (magnitude < 1) return 'bg-green-50';
    if (magnitude < 5) return 'bg-green-100';
    if (magnitude < 15) return 'bg-green-200';
    if (magnitude < 50) return 'bg-green-300';
    if (magnitude < 200) return 'bg-green-400';
    return 'bg-green-500';
  } else if (percent < 0) {
    if (magnitude < 1) return 'bg-red-50';
    if (magnitude < 5) return 'bg-red-100';
    if (magnitude < 15) return 'bg-red-200';
    if (magnitude < 50) return 'bg-red-300';
    if (magnitude < 200) return 'bg-red-400';
    return 'bg-red-500';
  }
  return 'bg-gray-50';
}

export default function StreamingSummaryTable({ type, audioOnly = false, group = null, isPgp = false, onAddClick = null }) {
  const [expandedRow, setExpandedRow] = useState(null);
  // const [expandedItem, setExpandedItem] = useState(null);
  const [artistFilter, setArtistFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [timeRange, setTimeRange] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'last_complete_week_streams', order: 'desc' });
  const [selectedMetrics, setSelectedMetrics] = useState(['audio']);
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('global');
  const [showExportModal, setShowExportModal] = useState(false);
  const [expandedRowTab, setExpandedRowTab] = useState('metrics'); // 'metrics' or 'songs'
  const [artistSongsSortConfig, setArtistSongsSortConfig] = useState({ key: 'global_last_complete_week_streams', order: 'desc' });
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'

  // Metric options
  const metricOptions = [
    { id: 'total', label: 'Total Streams' },
    { id: 'audio', label: 'Audio Streams' },
    { id: 'video', label: 'Video Streams' },
    { id: 'ad', label: 'Ad-Supported' },
    { id: 'premium', label: 'Premium' },
    { id: 'ondemand', label: 'On-Demand' },
    { id: 'programmed', label: 'Programmed' }
  ];

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  // Get the label of the first selected metric for display
  const selectedMetricLabel = metricOptions.find(opt => selectedMetrics.includes(opt.id))?.label || 'Select Type';

  // Debounce artist filter
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(artistFilter), 500);
    return () => clearTimeout(timer);
  }, [artistFilter]);

  // Reset expanded row tab when expanding a different row
  useEffect(() => {
    setExpandedRowTab('metrics');
    setArtistSongsSortConfig({ key: 'global_last_complete_week_streams', order: 'desc' }); // Reset sort when changing rows
  }, [expandedRow]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(
    ['summaries', type, debouncedFilter, sortConfig, selectedRegion, audioOnly, group, isPgp],
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        per_page: 20,
        artist: debouncedFilter || undefined,
        sort_by: sortConfig.key,
        sort_order: sortConfig.order,
        region: selectedRegion,
        audio_only: audioOnly
      };
      
      // Add group parameter if provided
      if (group) {
        params.group = group;
      }
      
      // Add is_pgp parameter if provided
      if (isPgp) {
        params.is_pgp = 'true';
      }
      
      const response = await axios.get(`${API_BASE_URL}/${type}-summaries`, {
        params
      });
      
      console.log('API Response:', response.data);
      return response.data;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.page < Math.ceil(lastPage.total / lastPage.per_page)
          ? lastPage.page + 1
          : undefined;
      }
    }
  );

  const { data: metrics } = useQuery(
    ['metrics', type, expandedRow, timeRange, selectedRegion, viewMode],
    async () => {
      if (!expandedRow || (type === 'artist' && expandedRowTab !== 'metrics')) return null;
      let endpoint;
      if (viewMode === 'weekly') {
        // For artists, use the on-catalog endpoint for weekly metrics
        if (type === 'artist') {
          endpoint = `${API_BASE_URL}/artist-metrics-weekly-on-catalog/${expandedRow}`;
        } else if (type === 'album') {
          endpoint = `${API_BASE_URL}/album-metrics-weekly/${expandedRow}`;
        } else {
          endpoint = `${API_BASE_URL}/song-metrics-weekly/${expandedRow}`;
        }
      } else {
        endpoint = `${API_BASE_URL}/${type}-metrics/${expandedRow}`;
      }
      const response = await axios.get(endpoint, {
        params: {
          days: timeRange,
          region: selectedRegion
        }
      });
      console.log('Metrics response:', response.data);
      return response.data;
    },
    { enabled: !!(expandedRow && (type === 'song' || type === 'album' || (type === 'artist' && expandedRowTab === 'metrics'))) }
  );

  // Query for artist songs when in artist view and songs tab is selected
  const { data: artistSongs } = useQuery(
    ['artist-songs', expandedRow, debouncedFilter, artistSongsSortConfig],
    async () => {
      if (!expandedRow || type !== 'artist' || expandedRowTab !== 'songs') return null;
      
      // Get the artist name from the current data
      const artist = data?.pages
        .flatMap(page => page.data)
        .find(item => item.id === expandedRow);
      
      if (!artist) return null;
      
      const response = await axios.get(`${API_BASE_URL}/song-summaries`, {
        params: {
          artist: artist.name,
          per_page: 50, // Get more songs for the artist
          sort_by: artistSongsSortConfig.key,
          sort_order: artistSongsSortConfig.order
        }
      });
      return response.data;
    },
    { enabled: !!(expandedRow && type === 'artist' && expandedRowTab === 'songs') }
  );

  // Debug log for selected metrics
  useEffect(() => {
    if (metrics && expandedRow) {
      console.log('Selected metrics:', selectedMetrics);
      console.log('Available metrics:', metrics);
    }
  }, [metrics, selectedMetrics, expandedRow]);

  // Intersection Observer for infinite scroll
  const observerTarget = React.useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleArtistSongsSort = (key) => {
    setArtistSongsSortConfig(prev => ({
      key,
      order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.order === 'desc' ? 
      <ArrowDownIcon className="h-4 w-4 inline ml-1" /> : 
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />;
  };

  const ArtistSongsSortIcon = ({ columnKey }) => {
    if (artistSongsSortConfig.key !== columnKey) return null;
    return artistSongsSortConfig.order === 'desc' ? 
      <ArrowDownIcon className="h-4 w-4 inline ml-1" /> : 
      <ArrowUpIcon className="h-4 w-4 inline ml-1" />;
  };

  const handleExport = async (format) => {
    try {
      const params = {
        artist: debouncedFilter || undefined,
        sort_by: sortConfig.key,
        sort_order: sortConfig.order,
        format: format,
        audio_only: audioOnly,
        region: selectedRegion
      };
      
      // Add group parameter if provided
      if (group) {
        params.group = group;
      }
      
      // Add is_pgp parameter if provided
      if (isPgp) {
        params.is_pgp = 'true';
      }
      
      const response = await axios.get(`${API_BASE_URL}/${type}-export`, {
        responseType: 'blob',
        params
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileExtension = format === 'csv' ? 'csv' : 'pdf';
      link.setAttribute('download', `streaming_${type}s_${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const handleRowClick = (item) => {
    if (expandedRow === item.id) {
      setExpandedRow(null);
      // setExpandedItem(null);
    } else {
      setExpandedRow(item.id);
      // setExpandedItem(item);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <input
          type="text"
          placeholder="Filter by artist name..."
          value={artistFilter}
          onChange={e => setArtistFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-auto"
        />
        <div className="flex gap-2 ml-4">
          {onAddClick && (
            <button
              onClick={onAddClick}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add {type === 'album' ? 'Album' : type === 'song' ? 'Song' : 'Artist'}
            </button>
          )}
          <button
            onClick={() => setShowExportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Choose Export Format</h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export as CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="relative">
        <div className="overflow-x-auto min-w-full">
          <table className="w-full divide-y divide-gray-200">
            <colgroup>
              {(type === 'song' || type === 'album') ? (
                <>
                  <col className="w-[15%]" /> {/* Artist */}
                  <col className="w-[15%]" /> {/* Song/Album */}
                </>
              ) : (
                <col className="w-[15%]" /> /* Artist only */
              )}
              <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* Global Last Week */}
              <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* Global % Change */}
              {!isPgp && (
                <>
                  <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* US Last Week */}
                  <col className={(type === 'song' || type === 'album') ? "w-[10%]" : "w-[15%]"} /> {/* US % Change */}
                </>
              )}
              {type === 'song' && (
                <col className="w-[12%]" />
              )}
              <col className={`hidden lg:table-cell ${(type === 'song' || type === 'album') ? 'w-[10%]' : 'w-[12%]'}`} />
              <col className={`hidden lg:table-cell ${(type === 'song' || type === 'album') ? 'w-[10%]' : 'w-[12%]'}`} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                {type === 'song' ? (
                  <>
                    <th
                      onClick={() => handleSort('artist')}
                      className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      Artist
                      <SortIcon columnKey="artist" />
                    </th>
                    <th
                      onClick={() => handleSort('title')}
                      className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      Song
                      <SortIcon columnKey="title" />
                    </th>
                  </>
                ) : type === 'album' ? (
                  <>
                    <th
                      onClick={() => handleSort('artist')}
                      className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      Artist
                      <SortIcon columnKey="artist" />
                    </th>
                    <th
                      onClick={() => handleSort('title')}
                      className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                    >
                      Album
                      <SortIcon columnKey="title" />
                    </th>
                  </>
                ) : (
                  <th
                    onClick={() => handleSort('name')}
                    className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer truncate"
                  >
                    Artist
                    <SortIcon columnKey="name" />
                  </th>
                )}
                <th onClick={() => handleSort('global_last_complete_week_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                  Global Last Week
                  <SortIcon columnKey="global_last_complete_week_streams" />
                </th>
                <th onClick={() => handleSort('global_week_over_week_change_percent')} className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                  Global % Change
                  <SortIcon columnKey="global_week_over_week_change_percent" />
                </th>
                {!isPgp && (
                  <>
                    <th onClick={() => handleSort('us_last_complete_week_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                      US Last Week
                      <SortIcon columnKey="us_last_complete_week_streams" />
                    </th>
                    <th onClick={() => handleSort('us_week_over_week_change_percent')} className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                      US % Change
                      <SortIcon columnKey="us_week_over_week_change_percent" />
                    </th>
                  </>
                )}
                {type === 'song' && (
                  <th onClick={() => handleSort('atd_streams')} className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                    ATD
                    <SortIcon columnKey="atd_streams" />
                  </th>
                )}
                <th onClick={() => handleSort('audio_streams_percent')} className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                  Audio %
                  <SortIcon columnKey="audio_streams_percent" />
                </th>
                <th onClick={() => handleSort('video_streams_percent')} className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                  Video %
                  <SortIcon columnKey="video_streams_percent" />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.pages.map((page, i) => (
                <React.Fragment key={i}>
                  {page.data.map(item => (
                    <React.Fragment key={item.id}>
                      <tr 
                        onClick={() => handleRowClick(item)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        {type === 'song' ? (
                          <>
                            <td
                              className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                            >
                              {item.artist}
                            </td>
                            <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate">
                              {item.title}
                            </td>
                          </>
                        ) : type === 'album' ? (
                          <>
                            <td
                              className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                            >
                              {item.artist}
                            </td>
                            <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate">
                              {item.title}
                            </td>
                          </>
                        ) : (
                          <td
                            className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm truncate`}
                          >
                            {item.name}
                          </td>
                        )}
                        <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-right">
                          {formatNumber(item.global_last_complete_week_streams)}
                        </td>
                        <td className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-center ${getPercentChangeColor(item.global_week_over_week_change_percent)}`}>
                          <span className="inline-block font-medium text-gray-900">
                            {formatPercent(item.global_week_over_week_change_percent, true)}
                          </span>
                        </td>
                        {!isPgp && (
                          <>
                            <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-right">
                              {formatNumber(item.us_last_complete_week_streams)}
                            </td>
                            <td className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-center ${getPercentChangeColor(item.us_week_over_week_change_percent)}`}>
                              <span className="inline-block font-medium text-gray-900">
                                {formatPercent(item.us_week_over_week_change_percent, true)}
                              </span>
                            </td>
                          </>
                        )}
                        {type === 'song' && (
                          <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-right">
                            {formatNumber(item.atd_streams)}
                          </td>
                        )}
                        <td className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-right">
                          {formatPercent(item.audio_streams_percent)}
                        </td>
                        <td className="hidden lg:table-cell px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-right">
                          {formatPercent(item.video_streams_percent)}
                        </td>
                      </tr>
                      {expandedRow === item.id && (
                        <tr>
                          <td colSpan={(type === 'song' || type === 'album') ? (isPgp ? 8 : 9) : (isPgp ? 6 : 7)} className="px-4 py-4">
                            <div className="space-y-4">
                              {/* Tabs for artists only */}
                              {type === 'artist' && (
                                <div className="border-b border-gray-200">
                                  <nav className="-mb-px flex space-x-8">
                                    <button
                                      onClick={() => setExpandedRowTab('metrics')}
                                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        expandedRowTab === 'metrics'
                                          ? 'border-blue-500 text-blue-600'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                      }`}
                                    >
                                      Metrics Chart
                                    </button>
                                    <button
                                      onClick={() => setExpandedRowTab('songs')}
                                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        expandedRowTab === 'songs'
                                          ? 'border-blue-500 text-blue-600'
                                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                      }`}
                                    >
                                      Artist Songs
                                    </button>
                                  </nav>
                                </div>
                              )}

                              {/* Content based on selected tab or always show for songs */}
                              {(type === 'song' || type === 'album' || expandedRowTab === 'metrics') && (
                                <>
                                  <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                      <select
                                        value={timeRange}
                                        onChange={e => setTimeRange(Number(e.target.value))}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value={30}>Last 30 days</option>
                                        <option value={90}>Last 90 days</option>
                                        <option value={180}>Last 180 days</option>
                                        <option value={365}>Last year</option>
                                        <option value={9999}>All Time</option>
                                      </select>

                                      <select
                                        value={selectedRegion}
                                        onChange={e => setSelectedRegion(e.target.value)}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value="global">Global</option>
                                        <option value="us">US</option>
                                      </select>

                                      <select
                                        value={viewMode}
                                        onChange={e => setViewMode(e.target.value)}
                                        className="w-1/3 px-4 py-2 border rounded-lg text-sm"
                                      >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                      </select>
                                    </div>

                                    <div className="relative">
                                      <button
                                        onClick={() => setIsMetricsOpen(!isMetricsOpen)}
                                        className="w-full px-4 py-2 border rounded-lg text-sm text-blue-600 bg-white flex justify-between items-center"
                                      >
                                        {selectedMetricLabel}
                                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                                      </button>
                                      
                                      {isMetricsOpen && (
                                        <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
                                          {metricOptions.map(option => (
                                            <button
                                              key={option.id}
                                              onClick={() => toggleMetric(option.id)}
                                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                selectedMetrics.includes(option.id) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                                              }`}
                                            >
                                              {option.label}
                                              {selectedMetrics.includes(option.id) && (
                                                <CheckIcon className="h-5 w-5 text-blue-600" />
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="h-[250px] sm:h-[400px] w-full">
                                    {metrics && (
                                      <MetricsChart
                                        dates={metrics.dates}
                                        metrics={metrics.metrics}
                                        selectedMetrics={selectedMetrics}
                                      />
                                    )}
                                  </div>
                                </>
                              )}

                              {/* Artist Songs Tab Content */}
                              {type === 'artist' && expandedRowTab === 'songs' && (
                                <div className="space-y-4">
                                  <h4 className="text-lg font-medium text-gray-900">Songs by {item.name}</h4>
                                  {artistSongs ? (
                                    <div className="overflow-x-auto min-w-full">
                                      <table className="w-full divide-y divide-gray-200">
                                        <colgroup>
                                          <col className="w-[30%]" /> {/* Song */}
                                          <col className="w-[17.5%]" /> {/* Global Last Week */}
                                          <col className="w-[17.5%]" /> {/* Global % Change */}
                                          <col className="w-[17.5%]" /> {/* US Last Week */}
                                          <col className="w-[17.5%]" /> {/* US % Change */}
                                        </colgroup>
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th 
                                              onClick={() => handleArtistSongsSort('title')}
                                              className="px-1 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              Song
                                              <ArtistSongsSortIcon columnKey="title" />
                                            </th>
                                            <th 
                                              onClick={() => handleArtistSongsSort('global_last_complete_week_streams')}
                                              className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              Global Last Week
                                              <ArtistSongsSortIcon columnKey="global_last_complete_week_streams" />
                                            </th>
                                            <th 
                                              onClick={() => handleArtistSongsSort('global_week_over_week_change_percent')}
                                              className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              Global % Change
                                              <ArtistSongsSortIcon columnKey="global_week_over_week_change_percent" />
                                            </th>
                                            <th 
                                              onClick={() => handleArtistSongsSort('us_last_complete_week_streams')}
                                              className="px-1 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              US Last Week
                                              <ArtistSongsSortIcon columnKey="us_last_complete_week_streams" />
                                            </th>
                                            <th 
                                              onClick={() => handleArtistSongsSort('us_week_over_week_change_percent')}
                                              className="px-1 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                            >
                                              US % Change
                                              <ArtistSongsSortIcon columnKey="us_week_over_week_change_percent" />
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {artistSongs.data.map(song => (
                                            <tr key={song.id} className="hover:bg-gray-50">
                                              <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-gray-900 truncate">{song.title}</td>
                                              <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-gray-900 text-right">{formatNumber(song.global_last_complete_week_streams)}</td>
                                              <td className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-center ${getPercentChangeColor(song.global_week_over_week_change_percent)}`}>
                                                <span className="inline-block font-medium text-gray-900">
                                                  {formatPercent(song.global_week_over_week_change_percent, true)}
                                                </span>
                                              </td>
                                              <td className="px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-gray-900 text-right">{formatNumber(song.us_last_complete_week_streams)}</td>
                                              <td className={`px-1 sm:px-4 py-2 sm:py-4 text-[11px] sm:text-sm text-center ${getPercentChangeColor(song.us_week_over_week_change_percent)}`}>
                                                <span className="inline-block font-medium text-gray-900">
                                                  {formatPercent(song.us_week_over_week_change_percent, true)}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="text-center py-8 text-gray-500">Loading songs...</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Expanded Row Content - Positioned absolutely */}
        
      </div>

      <div ref={observerTarget} className="h-4" />
      
      {isFetchingNextPage && (
        <div className="text-center py-4">Loading more...</div>
      )}
    </div>
  );
} 